import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Generate a presigned S3 POST URL for uploading an execution file to temp storage.
 * POST /api/:projectId/execution-file-upload/generate-upload-url
 *
 * Allowed MIME types: application/pdf, text/plain, application/vnd.ms-excel,
 * application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, image/png, image/jpeg
 * Max file size: 5MB (5 242 880 bytes)
 *
 * @param {Object} params
 * @param {string} params.fileName - Original file name with extension
 * @param {string} params.fileType - MIME type
 * @param {number} params.fileSize - File size in bytes
 * @returns {Promise<{status, data?: { uploadUrl, fields, fileKey, expiresIn, maxFileSize, allowedTypes }}>}
 */
export async function generateUploadUrl({ fileName, fileType, fileSize } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!fileName)   return { status: STATUS.ERROR, error: "File name is required" };
    if (!fileType)   return { status: STATUS.ERROR, error: "File type is required" };
    if (!fileSize)   return { status: STATUS.ERROR, error: "File size is required" };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/generate-upload-url`,
      { fileName, fileType, fileSize },
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error generating upload URL:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Upload a file directly to S3 using the presigned POST URL returned by generateUploadUrl.
 * This call goes directly to S3 — not through the backend.
 *
 * @param {Object} params
 * @param {string} params.uploadUrl - S3 presigned POST URL
 * @param {Object} params.fields    - Presigned POST form fields
 * @param {File}   params.file      - The file to upload
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function uploadFileToS3({ uploadUrl, fields, file } = {}) {
  try {
    if (!uploadUrl || !fields || !file) {
      return { success: false, error: "uploadUrl, fields, and file are required" };
    }

    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append("file", file);

    const response = await fetch(uploadUrl, { method: "POST", body: formData });

    if (response.ok || response.status === 204) {
      return { success: true };
    }

    return { success: false, error: `S3 upload failed with status ${response.status}` };
  } catch (error) {
    console.error("Error uploading file to S3:", error);
    return { success: false, error: error.message || "S3 upload failed" };
  }
}

/**
 * Move a temp S3 file to its permanent path and create an ExecutionFile record.
 * POST /api/:projectId/execution-file-upload/save
 *
 * If the same file content already exists in the folder, the existing record is returned
 * with duplicate: true (no second copy is stored).
 *
 * @param {Object} params
 * @param {string} params.tempFileKey       - S3 key from generateUploadUrl response (fileKey)
 * @param {string} params.folderId          - Target folder UUID
 * @param {string} [params.fileDescription] - Optional description (max 500 chars)
 * @param {string} [params.fileName]        - Optional custom display name to override the S3 key name
 * @param {{ size: number, mimeType?: string, originalName?: string }} [params.fileMetadata] - Optional file metadata (size, mimeType, originalName)
 * @returns {Promise<{status, data?: { executionFileId, fileKey, fileName, fileMetadata, duplicate }}>}
 */
export async function saveExecutionFile({ executionFileId, tempFileKey, folderId, fileDescription, fileName, fileMetadata } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)    return { status: STATUS.ERROR, error: "Project ID is required" };
    if (executionFileId !== undefined && !executionFileId) {
      return { status: STATUS.ERROR, error: "executionFileId is invalid" };
    }
    if (!tempFileKey)  return { status: STATUS.ERROR, error: "tempFileKey is required" };
    if (!folderId)     return { status: STATUS.ERROR, error: "folderId is required" };

    const payload = {
      ...(executionFileId !== undefined && { executionFileId }),
      tempFileKey,
      folderId,
      ...(fileDescription !== undefined && { fileDescription }),
      ...(fileName         !== undefined && { fileName }),
      ...(fileMetadata     != null && { fileMetadata }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/save`,
      payload,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error saving execution file:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Register an uploaded quarantine execution file so it appears in File Manager immediately.
 * POST /api/:projectId/execution-file-upload/register
 *
 * @param {Object} params
 * @param {string} params.tempFileKey       - S3 quarantine key
 * @param {string} params.folderId          - Target folder UUID
 * @param {string} [params.fileDescription] - Optional description
 * @param {string} [params.fileName]        - Optional display name override
 * @param {{ size: number, mimeType?: string }} [params.fileMetadata]
 * @returns {Promise<{status, data?: { id, fileName, fileKey, scannedStatus, ... } }>}
 */
export async function registerExecutionFile({ tempFileKey, folderId, fileDescription, fileName, fileMetadata } = {}) {
  try {
    const projectId = getProjectId();
    if (!projectId)    return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!tempFileKey)  return { status: STATUS.ERROR, error: "tempFileKey is required" };
    if (!folderId)     return { status: STATUS.ERROR, error: "folderId is required" };

    const payload = {
      tempFileKey,
      folderId,
      ...(fileDescription !== undefined && { fileDescription }),
      ...(fileName         !== undefined && { fileName }),
      ...(fileMetadata     != null && { fileMetadata }),
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/register`,
      payload,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }
    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error registering execution file:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Refresh/finalize scan for a registered execution file.
 * POST /api/:projectId/execution-file-upload/:executionFileId/finalize-scan
 */
/**
 * Get a presigned download URL for an execution file.
 * GET /api/:projectId/execution-file-upload/:executionFileId/download-url
 *
 * @param {Object} params
 * @param {string} params.executionFileId - UUID of the execution file
 * @returns {Promise<{status, data?: { downloadUrl, fileKey, fileName, expiresIn }}>}
 */
export async function getExecutionFileDownloadUrl({ executionFileId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)      return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!executionFileId) return { status: STATUS.ERROR, error: "Execution file ID is required" };

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/${executionFileId}/download-url`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error getting execution file download URL:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get execution files in dropdown format (suggestions + descriptions).
 * GET /api/:projectId/execution-file-upload/files/dropdown
 *
 * @returns {Promise<{status, data?: { suggestions: Array<{id, name}>, descriptions: Object<string, {fileType, description, size, folder}> }}>}
 */
export async function getExecutionFilesForDropdown() {
  try {
    const projectId = getProjectId();

    if (!projectId) return { status: STATUS.ERROR, error: "Project ID is required" };

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/files/dropdown`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error fetching execution files for dropdown:", error);
    return extractErrorInfo(error);
  }
}

/**
 * List all execution files for the project (all folders).
 * GET /api/:projectId/execution-file-upload/files
 *
 * @param {Object} params
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=50] - Results per page (max 100)
 * @returns {Promise<{status, data?: { files, total, page, limit }}>}
 */
export async function getExecutionFilesAll({ page, limit } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) return { status: STATUS.ERROR, error: "Project ID is required" };

    const queryParams = {};
    if (page !== undefined) queryParams.page = page;
    if (limit !== undefined) queryParams.limit = limit;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/files`,
      { params: queryParams, withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error listing all execution files:", error);
    return extractErrorInfo(error);
  }
}

/**
 * List execution files by IDs.
 * GET /api/:projectId/execution-file-upload/files/by-ids
 *
 * @param {Object} params
 * @param {string[]} params.fileIds - Array of execution file UUIDs
 * @returns {Promise<{status, data?: { files }}>}
 */
export async function getExecutionFilesByIds({ fileIds } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!fileIds || !Array.isArray(fileIds) || fileIds.length === 0) {
      return { status: STATUS.SUCCESS, data: { files: [] }, message: "No file IDs provided" };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/files/by-ids`,
      { params: { fileIds: fileIds.join(",") }, withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error fetching execution files by IDs:", error);
    return extractErrorInfo(error);
  }
}

/**
 * List execution files in a folder.
 * GET /api/:projectId/execution-file-upload/folder/:folderId/files
 *
 * @param {Object} params
 * @param {string} params.folderId - Folder UUID
 * @param {number} [params.page=1] - Page number
 * @param {number} [params.limit=50] - Results per page (max 100)
 * @returns {Promise<{status, data?: { files, total, page, limit }}>}
 */
export async function getExecutionFiles({ folderId, page, limit } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId) return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!folderId)  return { status: STATUS.ERROR, error: "Folder ID is required" };

    const queryParams = {};
    if (page !== undefined) queryParams.page = page;
    if (limit !== undefined) queryParams.limit = limit;

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/folder/${folderId}/files`,
      { params: queryParams, withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error listing execution files:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Update execution file metadata (name and/or description).
 * PUT /api/:projectId/execution-file-upload/:executionFileId
 *
 * @param {Object} params
 * @param {string} params.executionFileId - Execution file UUID
 * @param {string} [params.fileName] - New file name (max 200 chars)
 * @param {string|null} [params.fileDescription] - New description (max 500 chars)
 * @returns {Promise<{status, data?: { id, fileName, fileDescription, updatedAt }}>}
 */
export async function updateExecutionFile({ executionFileId, fileName, fileDescription } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)       return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!executionFileId) return { status: STATUS.ERROR, error: "Execution file ID is required" };

    const payload = {};
    if (fileName !== undefined) payload.fileName = fileName;
    if (fileDescription !== undefined) payload.fileDescription = fileDescription;

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/${executionFileId}`,
      payload,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error updating execution file:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Move an execution file to another folder.
 * PATCH /api/:projectId/execution-file-upload/:executionFileId/move
 *
 * @param {Object} params
 * @param {string} params.executionFileId - Execution file UUID
 * @param {string} params.targetFolderId  - Destination folder UUID
 * @returns {Promise<{status, data?: { id, fileName, folderId, moved }}>}
 */
export async function moveExecutionFile({ executionFileId, targetFolderId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)         return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!executionFileId)    return { status: STATUS.ERROR, error: "Execution file ID is required" };
    if (!targetFolderId)     return { status: STATUS.ERROR, error: "Target folder ID is required" };

    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/${executionFileId}/move`,
      { targetFolderId },
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error moving execution file:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Get test cases that use this execution file (automation plan / execution script).
 * GET /api/:projectId/execution-file-upload/:executionFileId/usage
 *
 * @param {Object} params
 * @param {string} params.executionFileId - Execution file UUID
 * @returns {Promise<{status, data?: { usedInTestCases: Array<{id, key, title}> }}>}
 */
export async function getExecutionFileUsage({ executionFileId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)       return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!executionFileId) return { status: STATUS.ERROR, error: "Execution file ID is required" };

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/${executionFileId}/usage`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error getting execution file usage:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Delete an execution file (soft-delete + S3 cleanup).
 * DELETE /api/:projectId/execution-file-upload/:executionFileId
 *
 * @param {Object} params
 * @param {string} params.executionFileId - Execution file UUID
 * @returns {Promise<{status, data?: { id, deleted }}>}
 */
export async function deleteExecutionFile({ executionFileId } = {}) {
  try {
    const projectId = getProjectId();

    if (!projectId)       return { status: STATUS.ERROR, error: "Project ID is required" };
    if (!executionFileId) return { status: STATUS.ERROR, error: "Execution file ID is required" };

    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/${projectId}/execution-file-upload/${executionFileId}`,
      { withCredentials: true }
    );

    if (response?.data?.success) {
      return { status: STATUS.SUCCESS, data: response.data.data, message: response.data.message };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
    };
  } catch (error) {
    console.error("Error deleting execution file:", error);
    return extractErrorInfo(error);
  }
}
